/** RTK Query endpoints for the project-level System Requirements Document. */
import type { CreativeDirectionResponse } from '../../components/CreativeInspiration/creativeDirectionTypes';
import type { ScopeReadingResponse } from '../../components/CreativeInspiration/scopeReadingTypes';
import { eventService } from '../../services/eventService';
import type { AppDispatch } from '../index';

import { api } from './api';

export interface RequirementsResponse {
  project_id: string;
  content: string;
  version: number;
  updated_at?: string | null;
  created_at?: string | null;
}

export interface UpdateRequirementsArgs {
  projectId: string;
  content: string;
}

export interface PendingProposalResponse {
  has_pending: boolean;
  diff?: string | null;
  summary?: { added: number; removed: number; hunks: number } | null;
  base_version?: number | null;
  source?: string | null;
  created_at?: string | null;
}

interface AcceptProposalResponse {
  version: number;
  content: string;
}

export const requirementsApi = api.injectEndpoints({
  // eslint-disable-next-line max-lines-per-function
  endpoints: (builder) => ({
    getRequirements: builder.query<RequirementsResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/requirements`,
      providesTags: (_result, _error, projectId) => [
        { type: 'Requirements' as const, id: projectId },
      ],
    }),
    updateRequirements: builder.mutation<RequirementsResponse, UpdateRequirementsArgs>({
      query: ({ projectId, content }) => ({
        url: `/api/v1/projects/${projectId}/requirements`,
        method: 'PUT',
        body: { content },
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(requirementsApi.util.upsertQueryData('getRequirements', projectId, data));
        } catch {
          // Mutation errors are exposed by the generated hook.
        }
      },
      invalidatesTags: (_result, error, { projectId }) => (
        error ? [{ type: 'Requirements' as const, id: projectId }] : []
      ),
    }),
    getPendingRequirementsProposal: builder.query<PendingProposalResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/srd/pending`,
      providesTags: (_result, _error, projectId) => [
        { type: 'RequirementsProposal' as const, id: projectId },
      ],
    }),
    acceptPendingRequirementsProposal: builder.mutation<
      AcceptProposalResponse,
      { projectId: string; sessionId?: string | null }
    >({
      query: ({ projectId, sessionId }) => ({
        url: `/api/v1/projects/${projectId}/srd/pending/accept`,
        method: 'POST',
        // Session the Accept was clicked from — the resume pipeline posts its
        // system messages there instead of the owner's oldest session.
        body: sessionId ? { session_id: sessionId } : {},
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(requirementsApi.util.upsertQueryData('getRequirements', projectId, {
            project_id: projectId,
            content: data.content,
            version: data.version,
          }));
        } catch {
          // Mutation errors are exposed by the generated hook.
        }
      },
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'RequirementsProposal' as const, id: projectId },
      ],
    }),
    rejectPendingRequirementsProposal: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `/api/v1/projects/${projectId}/srd/pending/reject`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, projectId) => [
        { type: 'RequirementsProposal' as const, id: projectId },
      ],
    }),
    proposeRequirementsChange: builder.mutation<{ status: string }, { projectId: string; prompt: string }>({
      query: ({ projectId, prompt }) => ({
        url: `/api/v1/projects/${projectId}/srd/propose`,
        method: 'POST',
        body: { prompt },
      }),
    }),
    // The Creative Director's look (Creative Inspiration sub-tab); `live` is the
    // last streamed payload so a reload mid-SRD restores the cards.
    getCreativeDirection: builder.query<CreativeDirectionResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/requirements/creative-direction`,
      providesTags: (_result, _error, projectId) => [
        { type: 'CreativeDirection' as const, id: projectId },
      ],
    }),
    // The personas and pages read from the brief (Personas & pages sub-tab); `live` restores a reload mid-reading.
    getScopeReading: builder.query<ScopeReadingResponse, string>({
      query: (projectId) => `/api/v1/projects/${projectId}/requirements/scope-reading`,
      providesTags: (_result, _error, projectId) => [{ type: 'ScopeReading' as const, id: projectId }],
    }),
  }),
});

export const {
  useAcceptPendingRequirementsProposalMutation,
  useGetCreativeDirectionQuery,
  useGetScopeReadingQuery,
  useGetRequirementsQuery,
  useGetPendingRequirementsProposalQuery,
  useLazyGetRequirementsQuery,
  useRejectPendingRequirementsProposalMutation,
  useUpdateRequirementsMutation,
  useProposeRequirementsChangeMutation,
} = requirementsApi;

// Project-scoped SSE listener. It is reference-counted so every Requirements
// consumer reads the same RTK cache without installing duplicate listeners.
const wiredProjects = new Map<string, { count: number; unsubscribe: () => void }>();

/** Fold one `requirements` SSE payload into the RTK cache (the Creative Direction read re-parses it). */
const syncRequirementsEvent = (
  dispatch: AppDispatch,
  projectId: string,
  source: Partial<RequirementsResponse>,
): void => {
  if (typeof source.content !== 'string' || typeof source.version !== 'number') {
    dispatch(api.util.invalidateTags([
      { type: 'Requirements', id: projectId },
      { type: 'CreativeDirection', id: projectId },
    ]));

    return;
  }

  dispatch(requirementsApi.util.upsertQueryData('getRequirements', projectId, {
    project_id: projectId,
    content: source.content,
    version: source.version,
    updated_at: source.updated_at,
    created_at: source.created_at,
  }));
  dispatch(api.util.invalidateTags([
    { type: 'ArtifactVersion', id: `${projectId}:srd` },
    { type: 'RequirementsProposal', id: projectId },
    { type: 'CreativeDirection', id: projectId },
  ]));
};

export function wireRequirementsSSE(projectId: string, dispatch: AppDispatch): () => void {
  eventService.connect(projectId);
  const existing = wiredProjects.get(projectId);

  if (existing) {
    existing.count += 1;
  } else {
    const unsubscribe = eventService.subscribe('requirements', (data: unknown) => {
      const payload = data as Partial<RequirementsResponse> & {
        data?: Partial<RequirementsResponse>;
      };
      const source = payload.data ?? payload;

      if (payload.project_id && payload.project_id !== projectId) return;
      syncRequirementsEvent(dispatch, projectId, source);
    });

    wiredProjects.set(projectId, { count: 1, unsubscribe });
  }

  return () => {
    const current = wiredProjects.get(projectId);

    if (!current) return;
    current.count -= 1;
    if (current.count > 0) return;
    current.unsubscribe();
    wiredProjects.delete(projectId);
  };
}
